/**
 * Report 161 group 5 (phone width at 200% text): a row with a label on the left and a
 * figure on the right never runs past a 360-wide phone. At normal text it looks as it
 * always did; when the two no longer fit side by side, the figure drops under the label
 * instead of pushing the row off the screen.
 *
 * `MONEY_ROW` goes on the row and `MONEY_LABEL` on its left-hand side, and an ancestor
 * carries `@container` (the card or list the row sits in). Every class in both is behind
 * a 14rem container step. A phone at normal text gives these containers 16rem or more
 * (a 360 screen: 18rem and up), so they do nothing there and the row is exactly as it
 * was. Large text halves the container in rem (a 390 screen at 200%: about 11rem), and
 * then the row may wrap and the label takes the room left by the figure. The label's
 * zero flex basis means the row breaks only when the label's longest word and the
 * figure can't share a line.
 *
 * The source half of the rule is `findMoneyRows`/`moneyRowProblems`, used by
 * `moneyRowGuard.test.ts`; the pixel half is `scripts/qa/sideways-sweep.mjs`.
 */
import { classNameOf, openingTag } from "@/lib/tabRowGuard";

export const MONEY_ROW = "flex justify-between @max-[14rem]:flex-wrap @max-[14rem]:gap-x-3";
export const MONEY_LABEL = "@max-[14rem]:min-w-0 @max-[14rem]:flex-1";

export type MoneyRow = { tag: string; line: number; classes: string; usesConstant: boolean };

/** The element starting at `start`, through its matching closing tag. */
function subtree(src: string, start: number, name: string, tag: string): string {
  if (tag.endsWith("/>")) return tag;
  const re = new RegExp(`<(/?)${name.replace(/\./g, "\\.")}\\b`, "g");
  re.lastIndex = start;
  let depth = 0;
  for (let m = re.exec(src); m; m = re.exec(src)) {
    if (m[1] === "/") {
      if (--depth === 0) return src.slice(start, src.indexOf(">", m.index) + 1);
    } else if (!openingTag(src, m.index).endsWith("/>")) depth++;
  }
  return src.slice(start);
}

/**
 * Every one-line row in a source file that spreads its children apart
 * (`flex … justify-between`, no `flex-col`) and holds a figure (`tabular-nums` anywhere
 * inside it). Rows built from `MONEY_ROW` are reported with `usesConstant`.
 */
export function findMoneyRows(src: string): MoneyRow[] {
  const rows: MoneyRow[] = [];
  for (const m of src.matchAll(/<([A-Za-z][\w.]*)\b/g)) {
    const tag = openingTag(src, m.index!);
    const usesConstant = /\bclassName=\{[^}]*\bMONEY_ROW\b/.test(tag);
    const classes = classNameOf(tag);
    const tokens = classes.split(/\s+/);
    const isSpreadRow =
      tokens.includes("justify-between") &&
      tokens.some((t) => t === "flex" || t === "inline-flex") &&
      !tokens.includes("flex-col");
    if (!usesConstant && !isSpreadRow) continue;
    const inner = subtree(src, m.index!, m[1], tag).slice(tag.length);
    if (!/\btabular-nums\b/.test(inner)) continue;
    const line = src.slice(0, m.index).split("\n").length;
    rows.push({ tag: m[1], line, classes, usesConstant });
  }
  return rows;
}

/** What is wrong with one row, or [] when it can wrap. */
export function moneyRowProblems(row: MoneyRow): string[] {
  if (row.usesConstant) return [];
  const tokens = row.classes.split(/\s+/);
  if (tokens.some((t) => /^(?:@[^:]*:)?flex-wrap$/.test(t))) return [];
  return ["is a one-line label/figure row (use MONEY_ROW and MONEY_LABEL)"];
}
